import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

type FormType =
  | "Service Request"
  | "Provenance Inquiry"
  | "DPP Transfer"
  | "Warranty Estimate"
  | "Appraisal Request";
type UIStatus = "New" | "In progress" | "Closed";

const formPillColors: Record<string, string> = {
  "Service Request": "var(--accent)",
  "Provenance Inquiry": "var(--ink-soft)",
  "DPP Transfer": "var(--warning)",
  "Warranty Estimate": "var(--success)",
  "Appraisal Request": "var(--danger)",
};

const statusColors: Record<UIStatus, string> = {
  New: "var(--accent)",
  "In progress": "var(--warning)",
  Closed: "var(--ink-mute)",
};

interface SubmissionItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  subject: string;
  status: UIStatus;
  createdTime: string;
}

interface SubmissionList {
  items: SubmissionItem[];
  total: number;
  page: number;
  limit: number;
}

interface SubmissionDetail extends SubmissionItem {
  phone: string | null;
  receiverFirstName: string | null;
  receiverLastName: string | null;
  receiverEmail: string | null;
  receiverPhone: string | null;
  watchBrand: string | null;
  watchModel: string | null;
  watchSerial: string | null;
  dppId: string | null;
  notes: string | null;
  datePurchased: string | null;
  serviceHistory: string | null;
  statusNote: string | null;
  updatedTime: string;
  closedTime: string | null;
}

const STATUS_OPTIONS: { ui: UIStatus; value: string }[] = [
  { ui: "New", value: "new" },
  { ui: "In progress", value: "in_progress" },
  { ui: "Closed", value: "closed" },
];

// Statuses only ever advance forward one step at a time — no skipping
// straight to Closed and no reopening a closed submission.
const STATUS_SEQUENCE: UIStatus[] = ["New", "In progress", "Closed"];

function nextStatusOption(
  current: UIStatus
): { ui: UIStatus; value: string } | null {
  const idx = STATUS_SEQUENCE.indexOf(current);
  const next = idx >= 0 ? STATUS_SEQUENCE[idx + 1] : undefined;
  return next ? (STATUS_OPTIONS.find((o) => o.ui === next) ?? null) : null;
}

const RANGES = ["24h", "7d", "30d", "All"] as const;
type Range = (typeof RANGES)[number];
const LIMIT = 20;

function rangeFromDate(range: Range): string | undefined {
  if (range === "All") return undefined;
  const hours = range === "24h" ? 24 : range === "7d" ? 168 : 720;
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}.${mo}.${day} · ${h}:${min}`;
}

function fmtAge(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "120px 1fr",
        gap: 16,
        alignItems: "baseline",
      }}
    >
      <span
        className="mono-cap text-mute"
        style={{ fontSize: 10, letterSpacing: "0.12em" }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 13,
          color: "var(--ink)",
          fontFamily: mono ? "var(--f-mono)" : "var(--f-body)",
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
    </div>
  );
}

interface DppPreviewData {
  content?: {
    name?: string;
    medias?: { url: string; type: string; mediaType: string }[];
  };
  minted?: boolean;
}

function DppPreview({
  brand,
  model,
  serial,
}: {
  brand: string;
  model: string;
  serial: string;
}) {
  const [data, setData] = useState<DppPreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    api
      .get<DppPreviewData>(
        `/admin/dpps/${encodeURIComponent(brand)}/${encodeURIComponent(model)}/${encodeURIComponent(serial)}`
      )
      .then(({ data: d }) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brand, model, serial]);

  const image = (data?.content?.medias ?? []).find((m) => m.url)?.url;
  const title = loading
    ? "Loading…"
    : failed
      ? "DPP unavailable"
      : (data?.content?.name ?? `${brand} ${model}`);

  return (
    <div style={{ display: "grid", gap: 6 }}>
      <span
        className="mono-cap text-mute"
        style={{ fontSize: 10, letterSpacing: "0.12em" }}
      >
        Passport
      </span>
      <Link
        to="/dpp"
        search={{ brand, model, serial }}
        style={{ textDecoration: "none" }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            border: "1px solid var(--line)",
            background: "var(--surface-2)",
            padding: 10,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              flexShrink: 0,
              background: "var(--surface-3)",
              overflow: "hidden",
            }}
          >
            {image && (
              <img
                src={image}
                alt={title}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
          </div>
          <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
            <span
              style={{
                fontSize: 13,
                color: "var(--ink)",
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </span>
            <span
              className="mono-cap"
              style={{
                fontSize: 10,
                letterSpacing: "0.12em",
                color: data?.minted ? "var(--success)" : "var(--ink-mute)",
              }}
            >
              {data?.minted ? "MINTED · " : ""}VIEW DPP →
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
}

export function SubmissionsPage() {
  const { can } = useAuth();
  const [range, setRange] = useState<Range>("7d");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [formFilter, setFormFilter] = useState("All forms");
  const [statusFilter, setStatusFilter] = useState("All status");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<SubmissionList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [pendingStatus, setPendingStatus] = useState<{
    ui: UIStatus;
    value: string;
  } | null>(null);
  const [statusNoteDraft, setStatusNoteDraft] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  function openSubmission(id: string) {
    setSelectedId(id);
    setDetail(null);
    setStatusError("");
    setPendingStatus(null);
    setStatusNoteDraft("");
    setDetailLoading(true);
    api
      .get<SubmissionDetail>(`/admin/submissions/${id}`)
      .then(({ data: d }) => setDetail(d))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }

  function closeSubmission() {
    setSelectedId(null);
    setDetail(null);
    setPendingStatus(null);
    setStatusNoteDraft("");
  }

  function confirmStatusChange() {
    if (!detail || !pendingStatus || statusSaving) return;
    const { value, ui } = pendingStatus;
    const note = statusNoteDraft.trim();
    setStatusError("");
    setStatusSaving(true);
    api
      .patch(`/admin/submissions/${detail.id}/status`, {
        status: value,
        ...(note ? { note } : {}),
      })
      .then(() => {
        setDetail({
          ...detail,
          status: ui,
          statusNote: note || detail.statusNote,
        });
        setPendingStatus(null);
        setStatusNoteDraft("");
        setReloadKey((k) => k + 1);
      })
      .catch((err: unknown) => {
        const code = (err as { response?: { status?: number } })?.response
          ?.status;
        setStatusError(
          code === 403
            ? "You don't have permission to change submission status."
            : "Failed to update status. Please try again."
        );
      })
      .finally(() => setStatusSaving(false));
  }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, formFilter, statusFilter, range]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    const params: Record<string, string | number> = { page, limit: LIMIT };
    if (debouncedSearch) params.search = debouncedSearch;
    if (formFilter !== "All forms") params.form = formFilter;
    if (statusFilter !== "All status") params.status = statusFilter;
    const fromDate = rangeFromDate(range);
    if (fromDate) params.from = fromDate;

    api
      .get<SubmissionList>("/admin/submissions", { params })
      .then(({ data: d }) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load submissions");
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, formFilter, statusFilter, range, reloadKey]);

  const totalPages = data ? Math.ceil(data.total / LIMIT) : 0;

  return (
    <>
      <div className="adm__body">
        <div className="adm__page-head">
          <div className="adm__title-stack">
            <div className="meta">↗ INBOUND · ROUTED TO OPERATIONS</div>
            <h1 className="h1" style={{ fontSize: 36, margin: 0 }}>
              Form submissions
            </h1>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
              flex: 1,
            }}
          >
            <div style={{ flex: 1, maxWidth: 460 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: "var(--surface-2)",
                  border: "1px solid var(--line)",
                  padding: "8px 14px",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  style={{ color: "var(--ink-mute)", flexShrink: 0 }}
                >
                  <circle cx="6" cy="6" r="4.5" />
                  <path d="m9.5 9.5 3 3" />
                </svg>
                <Input
                  type="search"
                  placeholder="Search by name, email or form…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    background: "transparent",
                    border: 0,
                    outline: 0,
                    color: "var(--ink)",
                    fontFamily: "var(--f-body)",
                    fontSize: 13,
                    width: "100%",
                    padding: 0,
                  }}
                />
              </div>
            </div>
            <select
              className="select"
              style={{
                width: "auto",
                padding: "9px 32px 9px 14px",
                fontSize: 12,
                backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12"><path fill="none" stroke="%236b7290" stroke-width="1.2" d="M3 5l3 3 3-3"/></svg>')`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                appearance: "none",
              }}
              value={formFilter}
              onChange={(e) => setFormFilter(e.target.value)}
            >
              {(
                [
                  "All forms",
                  "Service Request",
                  "Provenance Inquiry",
                  "DPP Transfer",
                  "Warranty Estimate",
                  "Appraisal Request",
                ] as const
              ).map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
            <select
              className="select"
              style={{
                width: "auto",
                padding: "9px 32px 9px 14px",
                fontSize: 12,
                backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12"><path fill="none" stroke="%236b7290" stroke-width="1.2" d="M3 5l3 3 3-3"/></svg>')`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                appearance: "none",
              }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {["All status", "New", "In progress", "Closed"].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                setFormFilter("All forms");
                setStatusFilter("All status");
              }}
            >
              Clear
            </Button>
          </div>

          <div className="seg">
            {RANGES.map((r) => (
              <button
                key={r}
                className={range === r ? "is-active" : ""}
                onClick={() => setRange(r)}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: "12px 16px",
              border: "1px solid var(--danger)",
              color: "var(--danger)",
              fontFamily: "var(--f-mono)",
              fontSize: 12,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead style={{ width: 32 }}>
                <input type="checkbox" />
              </TableHead>
              <TableHead>First name</TableHead>
              <TableHead>Last name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Form</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  style={{
                    textAlign: "center",
                    color: "var(--ink-mute)",
                    fontFamily: "var(--f-mono)",
                    fontSize: 11,
                    padding: "32px 0",
                  }}
                >
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              data?.items.map((row) => {
                const uiStatus = row.status as UIStatus;
                return (
                  <TableRow
                    key={row.id}
                    onClick={() => openSubmission(row.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" />
                    </TableCell>
                    <TableCell>{row.firstName}</TableCell>
                    <TableCell>{row.lastName}</TableCell>
                    <TableCell className="mono">{row.email}</TableCell>
                    <TableCell>
                      <span
                        style={{
                          fontFamily: "var(--f-mono)",
                          fontSize: 9.5,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          padding: "3px 8px",
                          border: `1px solid ${formPillColors[row.subject] ?? "var(--ink-mute)"}`,
                          color:
                            formPillColors[row.subject] ?? "var(--ink-mute)",
                        }}
                      >
                        {row.subject}
                      </span>
                    </TableCell>
                    <TableCell className="mono">
                      {fmtDate(row.createdTime)}
                    </TableCell>
                    <TableCell>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          fontFamily: "var(--f-mono)",
                          fontSize: 10,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: statusColors[uiStatus] ?? "var(--ink-mute)",
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            background:
                              statusColors[uiStatus] ?? "var(--ink-mute)",
                            display: "inline-block",
                            flexShrink: 0,
                          }}
                        />
                        {uiStatus}
                        {uiStatus !== "In progress"
                          ? ` · ${fmtAge(row.createdTime)}`
                          : ""}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            {!loading && data?.items.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  style={{
                    textAlign: "center",
                    color: "var(--ink-mute)",
                    fontFamily: "var(--f-mono)",
                    fontSize: 11,
                    padding: "32px 0",
                  }}
                >
                  No submissions found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <div
          className="row between mt-24 mono-cap text-mute"
          style={{ fontSize: 11 }}
        >
          <span>
            {data && data.total > 0
              ? `Showing ${(page - 1) * LIMIT + 1}–${Math.min(page * LIMIT, data.total)} of ${data.total}`
              : "Showing 0 of 0"}
          </span>
          <div className="row gap-12" style={{ alignItems: "center" }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={loading || page <= 1}
            >
              ‹ Prev
            </Button>
            <span style={{ minWidth: 88, textAlign: "center" }}>
              Page {data && data.total > 0 ? page : 0} of{" "}
              {Math.max(totalPages, data && data.total > 0 ? 1 : 0)}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={loading || page >= totalPages}
            >
              Next ›
            </Button>
          </div>
        </div>
      </div>

      <Modal
        open={selectedId !== null}
        title={detail?.subject ?? "Submission detail"}
        onClose={closeSubmission}
      >
        {detailLoading && (
          <div className="mono-cap text-mute" style={{ fontSize: 11 }}>
            Loading…
          </div>
        )}
        {!detailLoading && !detail && (
          <div className="mono-cap text-mute" style={{ fontSize: 11 }}>
            Failed to load submission
          </div>
        )}
        {!detailLoading && detail && (
          (() => {
            const hasReceiver = Boolean(
              detail.receiverEmail ||
                detail.receiverFirstName ||
                detail.receiverLastName
            );
            const receiverName =
              [detail.receiverFirstName, detail.receiverLastName]
                .filter(Boolean)
                .join(" ") || "—";
            return (
          <div style={{ display: "grid", gap: 18 }}>
            <DetailRow
              label={hasReceiver ? "Sender" : "Name"}
              value={`${detail.firstName} ${detail.lastName}`}
            />
            <DetailRow
              label={hasReceiver ? "Sender email" : "Email"}
              value={detail.email}
              mono
            />
            <DetailRow label="Phone" value={detail.phone ?? "—"} mono />
            {hasReceiver && (
              <>
                <div style={{ borderTop: "1px solid var(--line)" }} />
                <DetailRow label="Receiver" value={receiverName} />
                <DetailRow
                  label="Receiver email"
                  value={detail.receiverEmail ?? "—"}
                  mono
                />
                <DetailRow
                  label="Receiver phone"
                  value={detail.receiverPhone ?? "—"}
                  mono
                />
                <div style={{ borderTop: "1px solid var(--line)" }} />
              </>
            )}
            <DetailRow label="Form" value={detail.subject} />
            <DetailRow label="Watch brand" value={detail.watchBrand ?? "—"} />
            <DetailRow label="Watch model" value={detail.watchModel ?? "—"} />
            <DetailRow
              label="Watch serial"
              value={detail.watchSerial ?? "—"}
              mono
            />
            {detail.dppId &&
              detail.watchBrand &&
              detail.watchModel &&
              detail.watchSerial && (
                <DppPreview
                  brand={detail.watchBrand}
                  model={detail.watchModel}
                  serial={detail.watchSerial}
                />
              )}
            {detail.datePurchased && (
              <DetailRow
                label="Date acquired"
                value={detail.datePurchased}
                mono
              />
            )}
            <DetailRow
              label="Submitted"
              value={fmtDate(detail.createdTime)}
              mono
            />
            {detail.serviceHistory && (
              <div style={{ display: "grid", gap: 6 }}>
                <span
                  className="mono-cap text-mute"
                  style={{ fontSize: 10, letterSpacing: "0.12em" }}
                >
                  Service history
                </span>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--ink)",
                    whiteSpace: "pre-wrap",
                    border: "1px solid var(--line)",
                    background: "var(--surface-2)",
                    padding: "10px 12px",
                  }}
                >
                  {detail.serviceHistory}
                </div>
              </div>
            )}
            {detail.notes && (
              <div style={{ display: "grid", gap: 6 }}>
                <span
                  className="mono-cap text-mute"
                  style={{ fontSize: 10, letterSpacing: "0.12em" }}
                >
                  Notes
                </span>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--ink)",
                    whiteSpace: "pre-wrap",
                    border: "1px solid var(--line)",
                    background: "var(--surface-2)",
                    padding: "10px 12px",
                  }}
                >
                  {detail.notes}
                </div>
              </div>
            )}
            {detail.statusNote && (
              <div style={{ display: "grid", gap: 6 }}>
                <span
                  className="mono-cap text-mute"
                  style={{ fontSize: 10, letterSpacing: "0.12em" }}
                >
                  Status note
                </span>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--ink)",
                    whiteSpace: "pre-wrap",
                    border: "1px solid var(--line)",
                    background: "var(--surface-2)",
                    padding: "10px 12px",
                  }}
                >
                  {detail.statusNote}
                </div>
              </div>
            )}
            <div style={{ display: "grid", gap: 8 }}>
              <span
                className="mono-cap text-mute"
                style={{ fontSize: 10, letterSpacing: "0.12em" }}
              >
                Status
              </span>

              {pendingStatus ? (
                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ fontSize: 16, color: "var(--ink-soft)" }}>
                    Change status from{" "}
                    <strong
                      style={{
                        color: "var(--ink)",
                        textTransform: "uppercase",
                      }}
                    >
                      {detail.status}
                    </strong>{" "}
                    to{" "}
                    <strong
                      style={{
                        color: "var(--ink)",
                        textTransform: "uppercase",
                      }}
                    >
                      {pendingStatus.ui}
                    </strong>
                    ?
                  </div>
                  <textarea
                    className="select"
                    placeholder="Note (optional)"
                    value={statusNoteDraft}
                    onChange={(e) => setStatusNoteDraft(e.target.value)}
                    disabled={statusSaving}
                    rows={3}
                    style={{
                      width: "100%",
                      fontFamily: "var(--f-body)",
                      fontSize: 13,
                      padding: "10px 12px",
                      resize: "vertical",
                    }}
                  />
                  <div className="row gap-12">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={statusSaving}
                      onClick={() => {
                        setPendingStatus(null);
                        setStatusNoteDraft("");
                        setStatusError("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="default"
                      disabled={statusSaving}
                      onClick={confirmStatusChange}
                    >
                      {statusSaving
                        ? "Saving…"
                        : `Confirm → ${pendingStatus.ui}`}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="row gap-12" style={{ alignItems: "center" }}>
                  <span
                    style={{
                      fontFamily: "var(--f-mono)",
                      fontSize: 11,
                      letterSpacing: "0.1em",
                      color: statusColors[detail.status] ?? "var(--ink-mute)",
                    }}
                  >
                    {detail.status.toUpperCase()}
                  </span>
                  {nextStatusOption(detail.status) ? (
                    <Button
                      size="sm"
                      variant="default"
                      disabled={statusSaving || !can("submission:update")}
                      onClick={() =>
                        setPendingStatus(nextStatusOption(detail.status))
                      }
                    >
                      Advance to {nextStatusOption(detail.status)!.ui} →
                    </Button>
                  ) : (
                    <span
                      className="mono-cap text-mute"
                      style={{ fontSize: 11 }}
                    >
                      Closed — no further transitions
                    </span>
                  )}
                </div>
              )}

              {statusError && (
                <span
                  className="mono-cap"
                  style={{ fontSize: 11, color: "var(--danger)" }}
                >
                  {statusError}
                </span>
              )}
            </div>
          </div>
            );
          })()
        )}
      </Modal>
    </>
  );
}
